import { useEffect, useState } from "react";
import useTitleStore from "../../store/titleStore";
import useDebounce from "../../hooks/useDebounce";
import { useTableHeight } from "../../hooks/useTableHeight";
import { Button, Card, Col, Flex, Form, Grid, Input, message, Modal, Pagination, Row, Select, Table, Tag } from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { registrationService } from "../../services/registration.service";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { indonesiaRegionService } from "../../services/indonesia-region.service";
import RenderIf from "../../components/RenderIf";
import RegistrationModalFilter from "../../components/RegistrationModalFilter";
import { downloadBlob } from "../../utils/download";
import { businessAreaService } from "../../services/business-area.service";
import { userService } from "../../services/user.service";

const statusOpt = [
  { label: 'Survey', value: 'survey' },
  { label: 'Validasi', value: 'validating' },
  { label: 'Pemasangan', value: 'installation' },
  { label: 'Ditolak', value: 'reject' },
  { label: 'Terpasang', value: 'finish' },
]

export default function RegistrationAll(){

  const breakpoint = Grid.useBreakpoint()


  const { setTitle } = useTitleStore();

  const tableHeight = useTableHeight();

  const [responsiveFilter, setResponsiveFilter] = useState(false)
  const [exportModal, setExportModal] = useState(false)
  const [messageApi, messageHolder] = message.useMessage()
  const [keyword, setKeyword] = useState("");
  const [selectedCity, setSelectedCity] = useState('')
  const [selectedDistrict, setSelectedDistrict] = useState('')
  const debouncedKeyword = useDebounce(keyword, 500);

  const [filterForm] = Form.useForm()
  const [exportForm] = Form.useForm()

  const city = Form.useWatch('city', filterForm)
  const district = Form.useWatch('district', filterForm)
  const village = Form.useWatch('village', filterForm)

  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    filter: []
  })

  useEffect(() => {
    setTitle("Sambungan Baru - Sedang Diproses");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams({
      ...dataParams,
      filter: [
        {
          operator: 'orWhere',
          value: [
            {
              field: 'nama_kustomer',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'nomor_kustomer',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'tracking_id',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'rt_kustomer',
              operator: 'like',
              value: debouncedKeyword
            },
            {
              field: 'rw_kustomer',
              operator: 'like',
              value: debouncedKeyword
            },
          ]
        },
        {
          field: 'kota_kustomer',
          operator: 'like',
          value: city
        },
        {
          field: 'kecamatan_kustomer',
          operator: 'like',
          value: district
        },
        {
          field: 'kelurahan_kustomer',
          operator: 'like',
          value: village
        },
      ]
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKeyword, city, district, village]);


  const { data: registrationData , isLoading, isRefetching } = useQuery({
    queryKey: ['registration', dataParams],
    queryFn: () => registrationService.getAllData(dataParams),
    placeholderData: keepPreviousData,
  })

  const { data: abData , isLoading: abLoading } = useQuery({
    queryKey: ['businessArea'],
    queryFn: () => businessAreaService.fetchAll(),
  })

  const { data: surveyorData , isLoading: surveyorLoading } = useQuery({
    queryKey: ['surveyor'],
    queryFn: () => userService.getAllSurveyor(),
  })

  const { data: marketingData , isLoading: marketingLoading } = useQuery({
    queryKey: ['marketing'],
    queryFn: () => userService.getAllMarketing(),
  })

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page
    })
  }

  const { data: cities } = useQuery({
    queryKey: ['cities'],
    queryFn: () => indonesiaRegionService.getCities()
  })

  const { data: districts } = useQuery({
    queryKey: ['districts', selectedCity],
    queryFn: () => indonesiaRegionService.getDistricts(selectedCity),
    enabled: !!selectedCity
  })

  const { data: villages } = useQuery({
    queryKey: ['districts', selectedDistrict],
    queryFn: () => indonesiaRegionService.getSubdistricts(selectedDistrict),
    enabled: !!selectedDistrict
  })

  const exportMutation = useMutation({
    mutationFn: (val) => registrationService.exportAllData(val),
    onSuccess: ({ blob, filename }) => {
      downloadBlob(blob, filename)
      messageApi.success("Data berhasil diexport")
    }
  })

  useEffect(() => {

    filterForm.resetFields(['district', 'village'])

    if(city){
      const selected = cities.find(e => e.name == city)
      setSelectedCity(selected.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city])

  useEffect(() => {

    filterForm.resetFields(['village'])
    
    if(district){
      const selected = districts.find(e => e.name == district)
      setSelectedDistrict(selected.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [district])

  const handleExport = (values) => exportMutation.mutate(values)

  const handleCloseModal = () => {
    setExportModal(false)
    filterForm.resetFields()
  }

  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 80,
      fixed: breakpoint.md && 'left',
      render: (text, record, index) =>  index + 1,
    },
    {
      title: 'Tracking ID',
      dataIndex: 'tracking_id',
      width: 120,
      fixed: breakpoint.md && 'left',
    },
    {
      title: 'Nama Pelanggan',
      dataIndex: 'nama_kustomer',
      width: 210
    },
    {
      title: 'No Telepon',
      dataIndex: 'nomor_kustomer',
      width: 150
    },
    {
      title: 'Jalan',
      dataIndex: 'jalan_kustomer',
      width: 210
    },
    {
      title: 'No Rumah',
      dataIndex: 'no_rumah_kustomer',
      width: 150
    },
    { 
      title: 'Kota',
      dataIndex: 'kota_kustomer',
      width: 250,
    },
    { 
      title: 'Kelurahan',
      dataIndex: 'kelurahan_kustomer',
      width: 150,
    },
    { 
      title: 'PA/PC',
      dataIndex: 'pa_pc',
      width: 150,
    },
    { 
      title: 'RT/RW',
      width: 120,
      render: val => `${val.rt_kustomer?.toString().padStart(3, '0')}/${val.rw_kustomer?.toString().padStart(3, '0')}`
    },
    {
      title: 'Tanggal Pendaftaran',
      dataIndex: 'date',
      width: 180,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 150,
      render: (val) => {
        switch (val) {
          case 'survey':
            return <Tag color="cyan" >Survey</Tag>
          case 'validating':
            return <Tag color="cyan" >Validasi</Tag>
          case 'checking by officer':
            return <Tag color="cyan" >Validasi</Tag>
          case 'installation':
            return <Tag color="cyan" >Pemasangan</Tag>
          default:
            break;
        }
      }
    },
    {
      title: 'Longitude',
      dataIndex: 'longitude',
      width: 180,
    },
    {
      title: 'Latitude',
      dataIndex: 'latitude',
      width: 180,
    },
    {
      title: 'No KTP',
      dataIndex: 'no_ktp',
      width: 180,
    },
    {
      title: 'Surveyor',
      dataIndex: 'surveyor',
      width: 180,
    },
    {
      title: 'Marketing',
      dataIndex: 'marketing',
      width: 180,
    },
    {
      title: 'Area Bisnis',
      dataIndex: 'ab',
      width: 150,
    },
    {
      title: 'Jarak Bangunan Dengan Pipa (m)',
      dataIndex: 'jarak_bangunan_dengan_pipa',
      align: 'center',
      width: 150,
    },
    {
      title: 'Catatan Officer',
      dataIndex: 'officer_notes',
      width: 180,
    },
    // {
    //   className: 'last-cell-p',
    //   title: 'Aksi',
    //   align: 'right',
    //   width: 80,
    //   fixed: 'right',
    //   render: (record) => (
    //     <Button  
    //       icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
    //       color="primary"
    //       variant="filled"
    //       onClick={() => navigate('/registration/process/detail/'+record.id)} 
    //       size=""
    //     />
    //   )
    // },
  ]

  return (
    <>
      {messageHolder}
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
      >
        <RenderIf when={breakpoint.lg} >
          <Form form={filterForm} >
            <Flex justify="flex-start" gap={20} style={{ paddingInline: 24, marginBottom: 24 }} >
              <Input 
                style={{ width: 250 }} 
                prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
                placeholder="cari.."
                onChange={e => setKeyword(e.target.value)}
                value={keyword}
              />
              <Form.Item noStyle name={'city'} >
                <Select
                  allowClear
                  options={cities?.map(e => ({ label: e.name, value: e.name }))}
                  style={{ width: 200 }}
                  placeholder="Kota"
                />
              </Form.Item>
              <Form.Item noStyle name={'district'} >
                <Select
                  allowClear
                  options={districts?.map(e => ({ label: e.name, value: e.name }))}
                  style={{ width: 200 }}
                  placeholder="Kecamatan"
                  disabled={!city}
                />
              </Form.Item>
              <Form.Item name={'village'} noStyle >
                <Select
                  allowClear
                  options={villages?.map(e => ({ label: e.name, value: e.name }))}
                  style={{ width: 200 }}
                  placeholder="Kelurahan"
                  disabled={!district}
                />
              </Form.Item>
              <Button 
                variant="solid" 
                onClick={() => setExportModal(true)}
                color="primary" 
                icon={
                  <Icon
                    icon={'hugeicons:file-export'}
                    width={18}
                    height={18}
                    style={{ marginLeft: -5, marginBottom: -2 }}
                  />
                }
              >
                Export
              </Button>
            </Flex>
          </Form>
        </RenderIf>
        <RenderIf when={!breakpoint.lg} >
          <Flex justify="flex-end" gap={20} style={{ padding: '0 24px', marginBottom: 24 }} >
            <Button 
              variant="solid" 
              onClick={() => setExportModal(true)}
              color="primary" 
              icon={
                <Icon
                  icon={'hugeicons:file-export'}
                  width={18}
                  height={18}
                  style={{ marginLeft: -5, marginBottom: -2 }}
                />
              }
            >
              Export
            </Button>
            <Button 
              variant="outlined" 
              color="primary" 
              icon={<Icon icon="mynaui:filter-solid" width="22" height="22" />}
              onClick={() => setResponsiveFilter(true)}
            >
              Filter
            </Button>
          </Flex>
        </RenderIf>
        <Table
          id="registration-table"
          columns={columns}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={'id'}
          scroll={{ y: tableHeight }}
          dataSource={registrationData?.data}
          pagination={false}
          rowClassName={(record) => !record.is_open ? 'row-open' : ''}
        />
        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={registrationData?.pagination?.total}
          showSizeChanger
          pageSizeOptions={['10', '20', '50']}
          current={registrationData?.pagination?.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
      <RegistrationModalFilter
        form={filterForm}
        setKeyword={setKeyword}
        keyword={keyword}
        city={city}
        cities={cities}
        responsiveFilter={responsiveFilter}
        setResponsiveFilter={setResponsiveFilter}
        district={district}
        districts={districts}
        villages={villages}
      />
      <Modal
        open={exportModal}
        title="Export Data"
        centered
        styles={{
          body: {
            paddingTop: 14,
            paddingBottom: 14,
          }
        }}
        onOk={exportForm.submit}
        onCancel={handleCloseModal}
        okButtonProps={{
          loading: exportMutation.isPending
        }}
      >
        <Form
          form={exportForm}
          colon={false}
          labelAlign="left"
          onFinish={handleExport}
          layout="vertical"
        >
          <Row gutter={[20]} >
            <Col span={12} >
              <Form.Item rules={[{ required: true }]} name={'start'} label="Mulai" >
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={12} >
              <Form.Item 
                rules={[
                  { required: true },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const start = getFieldValue('start');
                      if (!value || !start || value > start) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Tanggal sampai harus lebih besar dari tanggal mulai")
                      );
                    },
                  }),
                ]} 
                label="Sampai" 
                name={'end'} 
              >
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={12} >
              <Form.Item 
                label="Area Bisnis" 
                name={'ab'} 
              >
                <Select
                  options={abData?.responseData?.map(e => ({ label: e.NAMA_AB, value: e.NAMA_AB }))}
                  loading={abLoading}
                  showSearch
                  optionFilterProp="label"
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12} >
              <Form.Item 
                label="Status" 
                name={'status'} 
              >
                <Select
                  options={statusOpt}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12} >
              <Form.Item 
                label="Surveyor" 
                name={'surveyorId'} 
              >
                <Select
                  options={surveyorData?.data?.map(e => ({ label: e.name, value: e.id }))}
                  showSearch
                  optionFilterProp="label"
                  loading={surveyorLoading}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12} >
              <Form.Item 
                label="Marketing" 
                name={'marketingId'} 
              >
                <Select
                  options={marketingData?.data?.map(e => ({ label: e.name, value: e.id }))}
                  showSearch
                  optionFilterProp="label"
                  loading={marketingLoading}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  )
}